/**
 * Descargas en el propio dispositivo, para leer sin conexion.
 *
 * Es distinto de la descarga del servidor: aquella deja el capitulo en el
 * VPS y sigue haciendo falta internet para verlo. Esto copia las paginas al
 * telefono.
 *
 * Ademas de las imagenes se guarda la ficha del capitulo (nombre, serie,
 * lista de paginas). Sin ella la app no podria ni abrir el capitulo sin
 * conexion, porque esos datos vienen de la API.
 */

import {
  obtenerCapitulo,
  obtenerCapitulos,
  obtenerPaginas,
  type CapituloConManga,
} from '../api/consultas'
import { almacen } from './almacen'
import { claveImagen, descargarImagen } from './imagenes'
import { clavePagina, estadosMejora, rutaMejorada, usarMejora } from '../api/mejora'
import { pareceTruncado, repararCapitulo } from './truncados'

const PREFIJO = 'local:cap:'

export interface CapituloDescargado {
  id: number
  nombre: string
  numero: number
  mangaId: number
  mangaTitulo: string
  portada: string | null
  paginas: string[]
  bytes: number
  guardadoEn: number
}

function clave(capituloId: number): string {
  return `${PREFIJO}${capituloId}`
}

export async function estaDescargado(capituloId: number): Promise<boolean> {
  return (await almacen.leerDato<CapituloDescargado>(clave(capituloId))) !== null
}

export async function fichaDescargada(capituloId: number): Promise<CapituloDescargado | null> {
  return almacen.leerDato<CapituloDescargado>(clave(capituloId))
}

export async function listarDescargados(): Promise<CapituloDescargado[]> {
  const claves = await almacen.claves(PREFIJO)
  const fichas: CapituloDescargado[] = []
  for (const c of claves) {
    const ficha = await almacen.leerDato<CapituloDescargado>(c)
    if (ficha) fichas.push(ficha)
  }
  return fichas.sort((a, b) => a.mangaTitulo.localeCompare(b.mangaTitulo) || a.numero - b.numero)
}

/** Capitulos descargados de una serie, para navegar entre ellos sin red. */
export async function hermanosDescargados(mangaId: number): Promise<CapituloDescargado[]> {
  return (await listarDescargados()).filter((c) => c.mangaId === mangaId)
}

export interface Progreso {
  hechas: number
  total: number
}

/**
 * Copia un capitulo entero al dispositivo.
 *
 * Se descarga pagina a pagina en vez de todas a la vez: son cientos de KB
 * cada una y lanzarlas en paralelo satura la cola compartida con el lector.
 */
export async function descargarCapitulo(
  capituloId: number,
  alProgresar?: (p: Progreso) => void,
): Promise<CapituloDescargado> {
  const capitulo: CapituloConManga = await obtenerCapitulo(capituloId)
  let paginas = await obtenerPaginas(capituloId)

  /*
   * Un capitulo que el servidor tenga descargado a medias hay que arreglarlo
   * antes de copiarlo, o el telefono se queda con la version mutilada y ahi
   * ya no hay rescate posible: sin conexion nadie puede volver a preguntarle
   * a la fuente. Importa sobre todo en "me voy sin internet", que baja
   * capitulos sin abrirlos y nunca veria el problema.
   *
   * Solo se comprueba en los descargados, que son los unicos que pueden
   * estarlo: los demas se resuelven contra la fuente cada vez.
   */
  if (capitulo.isDownloaded) {
    const hermanos = await obtenerCapitulos(capitulo.mangaId)
    if (pareceTruncado(capitulo, paginas.length, hermanos)) {
      paginas = await repararCapitulo(capituloId, paginas)
    }
  }

  /*
   * Si el capitulo esta mejorado, lo que se guarda en el movil son las paginas
   * mejoradas: quien se lleva un capitulo al metro lo quiere igual de bien que
   * en casa. Se guardan las rutas mejoradas en la ficha, asi que sin conexion
   * el lector las encuentra sin preguntar nada.
   *
   * Solo si esta **terminado**: a medias, las paginas que faltan devolverian
   * 404 y el capitulo se guardaria con huecos, que sin red no hay forma de
   * arreglar. Y en el tamano de este aparato, que es el que va a pintarlas.
   */
  if (usarMejora()) {
    const clavePrimera = paginas.length ? clavePagina(paginas[0]!) : null
    if (clavePrimera) {
      try {
        const estados = await estadosMejora(clavePrimera.manga, [clavePrimera.capitulo])
        if (estados[clavePrimera.capitulo]?.estado === 'lista') {
          paginas = paginas.map((ruta) => rutaMejorada(ruta))
        }
      } catch {
        // Sin servicio de mejora se guarda lo de siempre, que es mejor que
        // quedarse sin capitulo
      }
    }
  }

  let bytes = 0
  for (let i = 0; i < paginas.length; i++) {
    const blob = await descargarImagen(paginas[i]!)
    if (blob) bytes += blob.size
    alProgresar?.({ hechas: i + 1, total: paginas.length })
  }

  // La portada tambien, o la serie apareceria sin imagen en Descargas
  if (capitulo.manga.thumbnailUrl) await descargarImagen(capitulo.manga.thumbnailUrl)

  const ficha: CapituloDescargado = {
    id: capitulo.id,
    nombre: capitulo.name,
    numero: capitulo.chapterNumber,
    mangaId: capitulo.mangaId,
    mangaTitulo: capitulo.manga.title,
    portada: capitulo.manga.thumbnailUrl,
    paginas,
    bytes,
    guardadoEn: Date.now(),
  }
  await almacen.guardarDato(clave(capituloId), ficha)
  return ficha
}

export async function borrarDescargado(capituloId: number): Promise<void> {
  const ficha = await fichaDescargada(capituloId)
  if (!ficha) return

  // Las paginas se borran solo si no las usa otro capitulo descargado. En la
  // practica no se repiten, pero borrar a ciegas dejaria huecos negros si
  // alguna vez coincidieran.
  const otras = new Set<string>()
  for (const otra of await listarDescargados()) {
    if (otra.id === capituloId) continue
    for (const ruta of otra.paginas) otras.add(ruta)
  }

  for (const ruta of ficha.paginas) {
    if (!otras.has(ruta)) await almacen.borrar(claveImagen(ruta))
  }
  await almacen.borrar(clave(capituloId))
}

export async function bytesDescargados(): Promise<number> {
  return (await listarDescargados()).reduce((total, ficha) => total + ficha.bytes, 0)
}

/**
 * Adapta una ficha local al mismo tipo que devuelve la API.
 *
 * Asi el lector no necesita saber si los datos vienen del servidor o del
 * telefono. Los campos de progreso van en blanco porque solo los sabe el
 * servidor; sin conexion se completan con lo guardado en `progresoLocal`.
 */
export function fichaComoCapitulo(ficha: CapituloDescargado): CapituloConManga {
  return {
    id: ficha.id,
    name: ficha.nombre,
    chapterNumber: ficha.numero,
    scanlator: null,
    isRead: false,
    isDownloaded: true,
    isBookmarked: false,
    lastPageRead: leerProgresoLocal(ficha.id),
    pageCount: ficha.paginas.length,
    uploadDate: '0',
    sourceOrder: 0,
    // Sin conexion no se sabe cuando se leyo; solo se usa para ordenar por
    // recencia en la ficha, que sin red no se abre.
    lastReadAt: null,
    mangaId: ficha.mangaId,
    manga: { id: ficha.mangaId, title: ficha.mangaTitulo, thumbnailUrl: ficha.portada },
  }
}

/*
 * Progreso pendiente de sincronizar.
 *
 * Al leer sin conexion el servidor no se entera de por donde vas. Se apunta
 * en el dispositivo y se envia en cuanto vuelve la red, para no perder la
 * marca al cambiar de aparato.
 */
const CLAVE_PROGRESO = 'yomou.progreso.'
const CLAVE_PENDIENTES = 'yomou.progreso.pendientes'

export function leerProgresoLocal(capituloId: number): number {
  return Number(localStorage.getItem(CLAVE_PROGRESO + capituloId)) || 0
}

/** Marca local, para retomar la lectura aunque no haya red. */
export function apuntarProgresoLocal(capituloId: number, pagina: number) {
  localStorage.setItem(CLAVE_PROGRESO + capituloId, String(pagina))
}

/** Se llama solo cuando el envio al servidor falla. */
export function dejarPendiente(capituloId: number, pagina: number, leido: boolean) {
  const pendientes = leerPendientes()
  pendientes[capituloId] = { pagina, leido }
  localStorage.setItem(CLAVE_PENDIENTES, JSON.stringify(pendientes))
}

export function hayPendientes(): boolean {
  return Object.keys(leerPendientes()).length > 0
}

function leerPendientes(): Record<string, { pagina: number; leido: boolean }> {
  try {
    return JSON.parse(localStorage.getItem(CLAVE_PENDIENTES) ?? '{}')
  } catch {
    return {}
  }
}

/** Envia al servidor el progreso que quedo pendiente por falta de red. */
export async function sincronizarProgreso(
  enviar: (capituloId: number, pagina: number, leido: boolean) => Promise<void>,
): Promise<number> {
  const pendientes = leerPendientes()
  const ids = Object.keys(pendientes)
  if (ids.length === 0) return 0

  let enviados = 0
  for (const id of ids) {
    const dato = pendientes[id]!
    try {
      await enviar(Number(id), dato.pagina, dato.leido)
      delete pendientes[id]
      enviados++
    } catch {
      // Sigue sin red: se queda pendiente para el proximo intento
      break
    }
  }
  localStorage.setItem(CLAVE_PENDIENTES, JSON.stringify(pendientes))
  return enviados
}
